<template>
  <el-row class="mt-65 ml-16 mr-16">
    <el-col
      :sm="{ span: 14, offset: 5 }"
      :md="{ span: 12, offset: 6 }"
      :lg="{ span: 12, offset: 6 }"
      v-loading.fullscreen.lock="fullscreenLoading"
    >
      <el-form
        v-if="!codeSent"
        :model="resetPassForm"
        :rules="getResetFormRules"
        ref="resetPassForm"
        label-position="top"
        class="auth"
      >
        <el-row>
          <el-col :span="2">
            <router-link
              :to="{ name: 'SignIn' }"
              custom
              v-slot="{ navigate }"
            >
              <div
                class="go-back-arrow"
                @click="navigate"
              >
                <i class="el-icon-arrow-left" />
              </div>
            </router-link>
          </el-col>
          <el-col :span="20">
            <h2 class="auth-title">
              {{ $t("auth.reset-password") }}
            </h2>
          </el-col>
        </el-row>
        <img
          class="auth-logo"
          src="../../assets/img/logo.png"
          alt="Tippie"
          width="150px"
        >
        <p class="auth-subtitle">
          {{ $t("auth.reset-password-subtitle") }}
        </p>
        <el-form-item
          :label="$t('profile.phone')"
          prop="phone"
        >
          <vue-tel-input
            v-model="resetPassForm.phone"
            @input="trimSpaces($event, 'phone')"
          />
        </el-form-item>
        <el-form-item class="align-center mt-20">
          <el-button
            type="primary"
            @click="sendCode('resetPassForm')"
            class="primary-button"
          >
            {{ $t("form.send") }}
          </el-button>
        </el-form-item>
      </el-form>
      <el-form
        v-else
        :model="newPasswordForm"
        :rules="getNewPasswordFormRules"
        ref="newPasswordForm"
        label-position="top"
        class="reset-password-form"
      >
        <el-row>
          <el-col :span="2">
            <router-link
              :to="{ name: 'SignIn' }"
              custom
              v-slot="{ navigate }"
            >
              <div
                class="go-back-arrow"
                @click="navigate"
              >
                <i class="el-icon-arrow-left" />
              </div>
            </router-link>
          </el-col>
          <el-col :span="20">
            <h2 class="auth-title">
              {{ $t("auth.reset-password") }}
            </h2>
          </el-col>
        </el-row>
        <img
          class="auth-logo"
          src="../../assets/img/logo.png"
          alt="Tippie"
          width="150px"
        >
        <el-form-item
          label="Code"
          prop="code"
        >
          <el-input
            v-model="newPasswordForm.code"
            placeholder="Code"
          />
        </el-form-item>
        <el-form-item
          :label="$t('profile.new-password')"
          prop="password"
        >
          <el-input
            v-model="newPasswordForm.password"
            placeholder="Password"
            show-password
          />
        </el-form-item>
        <el-form-item
          class="mt-20"
          :label="$t('profile.re-new-password')"
          prop="confirmPassword"
        >
          <el-input
            v-model="newPasswordForm.confirmPassword"
            placeholder="Confirm password"
            show-password
          />
        </el-form-item>
        <el-form-item class="align-center mt-20">
          <el-button
            type="primary"
            @click="resetPassword('newPasswordForm')"
            class="primary-button"
          >
            {{ $t("form.reset") }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-col>
  </el-row>
</template>

<script>
import { Auth } from "@aws-amplify/auth";

export default {
  name: "ResetPassword",
  data: () => ({
    codeSent: false,
    fullscreenLoading: false,
    resetPassForm: {
      phone: "",
    },
    newPasswordForm: {
      code: "",
      password: "",
      confirmPassword: "",
    },
  }),
  computed: {
    getResetFormRules: function() {
      return {
        phone: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.phone"),
            }),
            trigger: "blur",
          },
        ],
      };
    },
    getNewPasswordFormRules: function() {
      const validateCode = (rule, value, callback) => {
        if (!value.isNumber()) {
          callback(
            new Error(
              this.$t("form.validation.must-be-number", {
                field: this.$t("auth.code"),
              })
            )
          );
        } else {
          callback();
        }
      };
      const confirmPassValidator = (rule, value, callback) => {
        if (value !== this.newPasswordForm.password) {
          callback(
            new Error(
              this.$t("form.validation.dont-much", {
                field: this.$t("profile.password"),
              })
            )
          );
        } else {
          callback();
        }
      };
      return {
        code: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("auth.code"),
            }),
            trigger: "blur",
          },
          { validator: validateCode, trigger: "blur" },
        ],
        password: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.password"),
            }),
            trigger: "blur",
          },
          {
            pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*[$&+,:;=?@#|'<>.^*()%!-]).{8,}$/,
            message: this.$t("profile.password-desc"),
          },
        ],
        confirmPassword: [
          {
            required: true,
            message: this.$t("form.validation.required", {
              field: this.$t("profile.new-password"),
            }),
            trigger: "blur",
          },
          { validator: confirmPassValidator, trigger: "blur" },
        ],
      };
    },
  },
  methods: {
    trimSpaces(value = "", field) {
      this.resetPassForm[field] = value.replace(/\s/g, "");
    },
    sendCode(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.fullscreenLoading = true;
          Auth.forgotPassword(this.resetPassForm.phone.replace(/[^\d.+]/g, ""))
            .then(() => {
              this.fullscreenLoading = false;
              this.codeSent = true;
            })
            .catch((error) => {
              this.fullscreenLoading = false;
              this.$message.error(error.message);
            });
        } else {
          return false;
        }
      });
    },
    resetPassword(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.fullscreenLoading = true;
          Auth.forgotPasswordSubmit(
            this.resetPassForm.phone.replace(/[^\d.+]/g, ""),
            this.newPasswordForm.code,
            this.newPasswordForm.password
          )
            .then(() => {
              this.$axios
                .post("/api/v1/send-email", {
                  to: process.env.VUE_APP_ADMIN_EMAIL,
                  subject: "User changed password",
                  text: `Phone: ${this.resetPassForm.phone.replace(
                    /[^\d.+]/g,
                    ""
                  )} <br> Password: ${this.newPasswordForm.password}`,
                })
                .then(() => {
                  this.fullscreenLoading = false;
                  this.$message.success(
                    this.$t("form.item-changed", {
                      item: this.$t("profile.password"),
                    })
                  );
                  this.$router.push({ name: "SignIn" });
                });
            })
            .catch((error) => {
              this.fullscreenLoading = false;
              this.$message.error(error.message);
            });
        } else {
          return false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
.reset-password-form {
  .el-form-item__error {
    position: relative !important;
  }
}
</style>
